import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ArrowUpRight, Linkedin } from "lucide-react";

const links = [
  { to: "/", label: "Manifesto", end: true },
  { to: "/intelligence", label: "Intelligence" },
  { to: "/media", label: "Media & Speaking" },
  { to: "/resources", label: "Resources" },
  { to: "/industries", label: "Industries" },
  { to: "/community", label: "Community" },
  { to: "/about", label: "About" },
];

const externalLinks = [
  { href: "https://ethicalainow.org", label: "Shop" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-[var(--eai-border)]" data-testid="site-navbar">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <span className="font-display text-2xl lg:text-3xl font-bold tracking-tight leading-none">
              Ethical AI <span className="text-[var(--eai-accent)]">Now</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8 xl:gap-10">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={`nav-${l.label.toLowerCase()}-link`}
                className={({ isActive }) =>
                  `font-mono-eai text-xs uppercase tracking-[0.18em] transition-colors duration-200 ${
                    isActive ? "text-[var(--eai-ink)]" : "text-[var(--eai-ink-2)] hover:text-[var(--eai-ink)]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {externalLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`nav-${l.label.toLowerCase()}-link`}
                className="font-mono-eai text-xs uppercase tracking-[0.18em] text-[var(--eai-ink-2)] hover:text-[var(--eai-ink)] transition-colors duration-200 inline-flex items-center gap-1"
              >
                {l.label}
                <ArrowUpRight size={12} strokeWidth={2} />
              </a>
            ))}
            <a
              href="https://www.linkedin.com/in/ethical-ai-now/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Matthew Kilkenny on LinkedIn"
              data-testid="nav-linkedin-link"
              className="w-9 h-9 inline-flex items-center justify-center border border-[var(--eai-border)] text-[var(--eai-ink-2)] hover:text-white hover:bg-[var(--eai-ink)] hover:border-[var(--eai-ink)] transition-colors duration-200"
            >
              <Linkedin size={15} strokeWidth={1.8} />
            </a>
            <Link
              to="/contact"
              className="eai-btn"
              data-testid="nav-contact-btn"
            >
              Contact
            </Link>
          </nav>

          <button
            className="lg:hidden p-2 -mr-2"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[var(--eai-border)] bg-white" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase()}-link`}
                className="font-mono-eai text-sm uppercase tracking-[0.18em]"
              >
                {l.label}
              </NavLink>
            ))}
            {externalLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase()}-link`}
                className="font-mono-eai text-sm uppercase tracking-[0.18em] inline-flex items-center gap-1.5"
              >
                {l.label}
                <ArrowUpRight size={14} strokeWidth={2} />
              </a>
            ))}
            <Link to="/contact" onClick={() => setOpen(false)} className="eai-btn w-fit" data-testid="mobile-contact-btn">
              Contact
            </Link>
            <a
              href="https://www.linkedin.com/in/ethical-ai-now/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-linkedin-link"
              className="inline-flex items-center gap-2 font-mono-eai text-sm uppercase tracking-[0.18em] text-[var(--eai-ink-2)] hover:text-[var(--eai-ink)]"
            >
              <Linkedin size={14} strokeWidth={1.8} /> Connect on LinkedIn
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
